/**
 * The Research tutorial runs as three short tours, one per stage of a first
 * question, because each stage's targets only exist once the one before it has
 * happened: the terms row opens when text arrives, and results mount when the
 * search finishes. A tour can only highlight what is mounted when it starts, so
 * one long tour would reach steps whose targets were never there.
 */
export type ResearchTutorialSegment = 'example' | 'compose' | 'results';

export type ResearchTutorialPhase = 'idle' | ResearchTutorialSegment | 'done';

type ResearchTutorialStep = {
  text: string;
  /**
   * Replaces Next with an action the step's zone performs, and ends the
   * segment once it runs. The zone supplies the handler, since only the
   * surface it wraps can perform it.
   */
  actionLabel?: string;
};

type ResearchTutorialSegmentDefinition = {
  tourKey: string;
  next: ResearchTutorialPhase;
  steps: Record<number, ResearchTutorialStep>;
};

export const RESEARCH_TUTORIAL_SEGMENTS: Record<
  ResearchTutorialSegment,
  ResearchTutorialSegmentDefinition
> = {
  example: {
    tourKey: 'research-example',
    next: 'compose',
    steps: {
      1: {
        text: "Research searches specialists' answered inquiries for cases like yours. Here's how a search works, start to finish.",
      },
      2: {
        text: "Not sure where to start? Try an example. We'll put it in the box for you.",
        actionLabel: 'Use example',
      },
    },
  },
  compose: {
    tourKey: 'research-compose',
    next: 'results',
    steps: {
      1: {
        text: 'Your question goes here. For a case, include age, stage, key markers, what has been tried, and the decision you face.',
      },
      2: {
        text: 'Tick the box to confirm there is no patient-identifiable information.',
      },
      3: {
        text: 'Send it. The search takes a few seconds.',
        actionLabel: 'Send',
      },
    },
  },
  results: {
    tourKey: 'research-results',
    next: 'done',
    steps: {
      1: {
        text: 'These are the answered inquiries that match your question.',
      },
      2: {
        text: "Tap a related inquiry to read the submitter's case and the specialist's answer.",
      },
      3: {
        text: 'Still unsure? Send your question to a specialist as a new inquiry.',
      },
    },
  },
};

const SEGMENT_BY_TOUR_KEY = Object.fromEntries(
  (Object.keys(RESEARCH_TUTORIAL_SEGMENTS) as ResearchTutorialSegment[]).map((segment) => [
    RESEARCH_TUTORIAL_SEGMENTS[segment].tourKey,
    segment,
  ])
) as Record<string, ResearchTutorialSegment>;

export function segmentForTourKey(
  tourKey: string | undefined
): ResearchTutorialSegment | undefined {
  return tourKey ? SEGMENT_BY_TOUR_KEY[tourKey] : undefined;
}

export function zoneKey(segment: ResearchTutorialSegment, zone: number): string {
  return `${segment}:${zone}`;
}
